import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, ShieldCheck, MapPin, FileCheck2, Camera, ClipboardCheck } from "lucide-react";
import { SERVICES, CASE_STUDIES } from "@/lib/content";
import CoverageMap from "@/components/CoverageMap";

function StatPill({ k, v }) {
  return (
    <div className="border border-white/10 px-4 py-3">
      <div className="text-[10px] uppercase tracking-[0.24em] text-slate-400">{k}</div>
      <div className="font-display text-2xl text-white tabular-nums mt-1">{v}</div>
    </div>
  );
}

export default function HomePage() {
  return (
    <>
      {/* HERO */}
      <section data-testid="hero-section" className="bg-[#0B192C] text-white grain-overlay">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-2 border border-white/15 px-3 py-1 text-[10px] uppercase tracking-[0.28em] text-slate-300">
              <ShieldCheck className="h-3.5 w-3.5" />
              Institutional field services since 2011
            </div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl tracking-tighter font-semibold leading-[1.02] mt-6">
              Property preservation,{" "}
              <span className="text-slate-400">documented to the pixel.</span>
            </h1>
            <p className="mt-6 text-slate-300 text-base md:text-lg max-w-xl leading-relaxed">
              Mead Property Preservation LLC delivers audit-ready inspections,
              maintenance, and REO field services for financial institutions and
              mortgage servicers across New Jersey and Pennsylvania.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/quote"
                data-testid="hero-quote-btn"
                className="inline-flex items-center gap-2 px-5 py-3 bg-white text-[#0B192C] text-sm font-semibold rounded-sm hover:bg-slate-100"
              >
                Request a quote <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-services-btn"
                className="inline-flex items-center gap-2 px-5 py-3 border border-white/20 text-white text-sm font-medium rounded-sm hover:bg-white/5"
              >
                Explore services <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-2 max-w-xl">
              <StatPill k="Assets serviced" v="14,200+" />
              <StatPill k="Avg. TAT" v="42h" />
              <StatPill k="States covered" v="2" />
              <StatPill k="First-pass rate" v="99.4%" />
            </div>
          </div>

          <div className="md:col-span-5">
            <div className="relative aspect-[4/5] w-full overflow-hidden border border-white/10">
              <img
                src="https://images.pexels.com/photos/8961008/pexels-photo-8961008.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=720"
                alt="Field technician documenting a property"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute bottom-4 left-4 right-4 bg-[#0B192C]/85 backdrop-blur px-4 py-3 border border-white/10">
                <div className="text-[10px] uppercase tracking-[0.28em] text-slate-400">
                  Field packet · WO-042
                </div>
                <div className="text-sm text-white mt-1">
                  32 photos · GPS verified · Signed by tech
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Trust bar */}
        <div className="border-t border-white/10">
          <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-xs text-slate-500 uppercase tracking-[0.2em]">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4" /> HUD/FHA aligned
            </div>
            <div className="flex items-center gap-2">
              <FileCheck2 className="h-4 w-4" /> Audit-ready packets
            </div>
            <div className="flex items-center gap-2">
              <Camera className="h-4 w-4" /> GPS-verified photos
            </div>
            <div className="flex items-center gap-2">
              <ClipboardCheck className="h-4 w-4" /> Portal-first workflow
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES — Bento */}
      <section data-testid="services-preview" className="mx-auto max-w-[1400px] px-6 md:px-10 py-20">
        <div className="grid md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8">
            <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500">
              01 · Service catalog
            </div>
            <h2 className="font-display text-3xl md:text-5xl tracking-tighter font-medium mt-3">
              A complete field-services stack for defaulted &amp; REO assets.
            </h2>
          </div>
          <div className="md:col-span-4 text-sm text-slate-500 md:text-right">
            Priced per property, per visit, or under a portfolio SLA.
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-4">
          {/* Bento asymmetric layout */}
          <BentoCard service={SERVICES[0]} className="md:col-span-7 md:row-span-2 min-h-[420px]" featured />
          <BentoCard service={SERVICES[1]} className="md:col-span-5" />
          <BentoCard service={SERVICES[2]} className="md:col-span-5" />
          <BentoCard service={SERVICES[3]} className="md:col-span-4" />
          <BentoCard service={SERVICES[4]} className="md:col-span-4" />
          <BentoCard service={SERVICES[5]} className="md:col-span-4" />
          <BentoCard service={SERVICES[6]} className="md:col-span-12" wide />
        </div>

        <div className="mt-10">
          <Link
            to="/services"
            data-testid="services-viewall-btn"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#1E40AF] hover:text-[#1E3A8A]"
          >
            View full service catalog <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* COVERAGE */}
      <section id="coverage" className="bg-[#F8FAFC] border-y border-slate-200">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-20">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500">
                02 · Coverage footprint
              </div>
              <h2 className="font-display text-3xl md:text-5xl tracking-tighter font-medium mt-3">
                2 states. 88 counties. One accountability layer.
              </h2>
              <p className="mt-6 text-slate-600 leading-relaxed">
                Regional crew hubs across New Jersey and Pennsylvania keep
                turnaround times consistent even for high-volume portfolios and
                emergency dispatch requests.
              </p>
              <div className="mt-8 space-y-3 text-sm">
                <Row k="Standard TAT" v="48h from work order acceptance" />
                <Row k="Emergency response" v="< 24h for boarding &amp; secure" />
                <Row k="Portfolio SLAs" v="Custom terms available" />
              </div>
              <Link
                to="/coverage"
                data-testid="coverage-details-btn"
                className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#1E40AF]"
              >
                Coverage details <MapPin className="h-4 w-4" />
              </Link>
            </div>
            <div className="md:col-span-7">
              <CoverageMap compact />
            </div>
          </div>
        </div>
      </section>

      {/* CASE STUDIES */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 py-20">
        <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500">
          03 · Client outcomes
        </div>
        <h2 className="font-display text-3xl md:text-5xl tracking-tighter font-medium mt-3 max-w-3xl">
          Trusted by servicers and asset managers who audit every deliverable.
        </h2>
        <div className="mt-12 grid md:grid-cols-3 gap-4">
          {CASE_STUDIES.map((c) => (
            <article
              key={c.client}
              data-testid={`case-${c.client.replace(/\s+/g, "-").toLowerCase()}`}
              className="bg-white border border-slate-200 p-6 flex flex-col"
            >
              <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500">
                {c.client}
              </div>
              <div className="mt-4 font-display text-5xl tracking-tighter text-[#0B192C] tabular-nums">
                {c.metric}
              </div>
              <div className="text-xs text-slate-500 mt-1">{c.label}</div>
              <div className="mt-6 pt-6 border-t border-slate-200 flex-1">
                <p className="text-sm text-slate-700 leading-relaxed">“{c.quote}”</p>
                <div className="mt-4 text-xs text-slate-500">— {c.role}</div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0B192C] text-white grain-overlay">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-20 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <h2 className="font-display text-3xl md:text-5xl tracking-tighter font-medium">
              Ready to onboard your portfolio?
            </h2>
            <p className="mt-4 text-slate-300 max-w-xl">
              Send us your service scope and coverage requirements. Most quotes
              turn around inside 24 business hours.
            </p>
          </div>
          <div className="md:col-span-4 flex md:justify-end gap-3">
            <Link
              to="/quote"
              data-testid="cta-quote-btn"
              className="inline-flex items-center gap-2 px-5 py-3 bg-white text-[#0B192C] text-sm font-semibold rounded-sm hover:bg-slate-100"
            >
              Request a quote <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link
              to="/contact"
              data-testid="cta-contact-btn"
              className="inline-flex items-center gap-2 px-5 py-3 border border-white/20 text-white text-sm font-medium rounded-sm hover:bg-white/5"
            >
              Contact us
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-baseline justify-between border-b border-slate-200 py-2">
      <div className="text-xs uppercase tracking-[0.2em] text-slate-500">{k}</div>
      <div className="text-sm text-slate-800" dangerouslySetInnerHTML={{ __html: v }} />
    </div>
  );
}

function BentoCard({ service, className = "", featured = false, wide = false }) {
  return (
    <Link
      to="/services"
      data-testid={`bento-${service.key}`}
      className={`group relative overflow-hidden border border-slate-200 bg-white ${className}`}
    >
      {featured && (
        <img
          src={service.image}
          alt={service.title}
          className="absolute inset-0 h-full w-full object-cover opacity-90 group-hover:scale-[1.02] transition-transform duration-500"
        />
      )}
      {featured && <div className="absolute inset-0 bg-[#0B192C]/55" />}
      <div
        className={`relative h-full flex flex-col ${
          featured ? "text-white p-8" : "p-6"
        } ${wide ? "md:flex-row md:items-end md:justify-between" : ""}`}
      >
        <div className={wide ? "md:max-w-2xl" : ""}>
          <div
            className={`text-[10px] uppercase tracking-[0.28em] ${
              featured ? "text-slate-300" : "text-slate-500"
            }`}
          >
            {service.key.toUpperCase()}
          </div>
          <h3
            className={`font-display tracking-tight mt-3 ${
              featured ? "text-3xl md:text-4xl" : "text-xl md:text-2xl"
            } font-medium`}
            dangerouslySetInnerHTML={{ __html: service.title }}
          />
          <p
            className={`mt-3 text-sm leading-relaxed ${
              featured ? "text-slate-200 max-w-lg" : "text-slate-600"
            }`}
          >
            {service.short}
          </p>
        </div>
        <div
          className={`${
            wide ? "md:mt-0 mt-6" : "mt-6"
          } inline-flex items-center gap-2 text-sm font-semibold ${
            featured ? "text-white" : "text-[#1E40AF]"
          }`}
        >
          Learn more <ArrowUpRight className="h-4 w-4" />
        </div>
      </div>
    </Link>
  );
}
